// iot-admin/client/src/components/GGOSDeviceModals.js
// Modals for GGOS device actions: Deactivate and View Certificate

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { ggosDeviceService } from '../services/api';
import './GGOSDeviceModals.css';

// ============================================================
// DeactivateDeviceModal
// ============================================================
export const DeactivateDeviceModal = ({ isOpen, onClose, device, onSuccess }) => {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleClose = () => {
    setReason('');
    setError(null);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) {
      setError('Please provide a reason for deactivation');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await ggosDeviceService.deactivate(device.thingName, reason.trim());
      handleClose();
      onSuccess?.();
    } catch (err) {
      console.error('Deactivation error:', err);
      setError(err.response?.data?.error || 'Failed to deactivate device');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="ggos-modal-overlay" onClick={handleClose}>
      <div className="ggos-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ggos-modal__header">
          <h2 className="ggos-modal__title">⚠️ Deactivate Device</h2>
          <button className="ggos-modal__close" onClick={handleClose} aria-label="Close">×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="ggos-modal__body">
            <div className="ggos-modal__warning">
              <p>
                You are about to deactivate <strong>{device?.thingName}</strong>.
                This action will:
              </p>
              <ul>
                <li>Prevent the device from connecting to IoT Core</li>
                <li>Disable license validation for this device</li>
                <li>Mark the device as inactive in the system</li>
              </ul>
              <p><strong>This action cannot be easily undone.</strong></p>
            </div>

            {error && <div className="ggos-modal__error">{error}</div>}

            <div className="form-group">
              <label htmlFor="deactivate-reason">Reason for deactivation *</label>
              <textarea
                id="deactivate-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Device reported lost, hardware failure, returned to inventory…"
                rows={3}
                disabled={loading}
              />
            </div>
          </div>

          <div className="ggos-modal__footer">
            <button type="button" className="btn btn-secondary" onClick={handleClose} disabled={loading}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-danger"
              disabled={loading || !reason.trim()}
            >
              {loading ? 'Deactivating…' : 'Deactivate Device'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

DeactivateDeviceModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  device: PropTypes.shape({ thingName: PropTypes.string }),
  onSuccess: PropTypes.func,
};

// ============================================================
// ReactivateDeviceModal
// ============================================================
export const ReactivateDeviceModal = ({ isOpen, onClose, device, onSuccess }) => {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleClose = () => {
    setReason('');
    setError(null);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) {
      setError('Please provide a reason for reactivation');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await ggosDeviceService.reactivate(device.thingName, reason.trim());
      handleClose();
      onSuccess?.();
    } catch (err) {
      console.error('Reactivation error:', err);
      setError(err.response?.data?.error || 'Failed to reactivate device');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="ggos-modal-overlay" onClick={handleClose}>
      <div className="ggos-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ggos-modal__header">
          <h2 className="ggos-modal__title">▶️ Reactivate Device</h2>
          <button className="ggos-modal__close" onClick={handleClose} aria-label="Close">×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="ggos-modal__body">
            <p>Reactivate <strong>{device?.thingName}</strong> to allow it to connect to IoT Core again.</p>

            {error && <div className="ggos-modal__error">{error}</div>}

            <div className="form-group">
              <label htmlFor="reactivate-reason">Reason for reactivation *</label>
              <textarea
                id="reactivate-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Device recovered, hardware replaced…"
                rows={3}
                disabled={loading}
              />
            </div>
          </div>

          <div className="ggos-modal__footer">
            <button type="button" className="btn btn-secondary" onClick={handleClose} disabled={loading}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || !reason.trim()}
            >
              {loading ? 'Reactivating…' : 'Reactivate Device'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

ReactivateDeviceModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  device: PropTypes.shape({ thingName: PropTypes.string }),
  onSuccess: PropTypes.func,
};

// ============================================================
// ViewCertificateModal
// ============================================================
export const ViewCertificateModal = ({ isOpen, onClose, device }) => {
  if (!isOpen || !device) return null;

  const maskValue = (value, visibleChars = 12) => {
    if (!value) return 'N/A';
    if (value.length <= visibleChars * 2) return value;
    return `${value.slice(0, visibleChars)}…${value.slice(-visibleChars)}`;
  };

  const copyToClipboard = (text) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).catch(() => {});
    }
  };

  return (
    <div className="ggos-modal-overlay" onClick={onClose}>
      <div className="ggos-modal ggos-modal--wide" onClick={(e) => e.stopPropagation()}>
        <div className="ggos-modal__header">
          <h2 className="ggos-modal__title">🔐 Certificate Information</h2>
          <button className="ggos-modal__close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="ggos-modal__body">
          <div className="cert-info">
            <div className="cert-info__item">
              <span className="cert-info__label">Certificate ID</span>
              <div className="cert-info__value-row">
                <code className="cert-info__value">{maskValue(device.certificateId, 16)}</code>
                {device.certificateId && (
                  <button
                    className="cert-info__copy"
                    onClick={() => copyToClipboard(device.certificateId)}
                    title="Copy full value"
                  >📋</button>
                )}
              </div>
            </div>

            <div className="cert-info__item">
              <span className="cert-info__label">Certificate ARN</span>
              <div className="cert-info__value-row">
                <code className="cert-info__value cert-info__value--small">
                  {maskValue(device.certificateArn, 20)}
                </code>
                {device.certificateArn && (
                  <button
                    className="cert-info__copy"
                    onClick={() => copyToClipboard(device.certificateArn)}
                    title="Copy full value"
                  >📋</button>
                )}
              </div>
            </div>

            <div className="cert-info__item">
              <span className="cert-info__label">Thing ARN</span>
              <div className="cert-info__value-row">
                <code className="cert-info__value cert-info__value--small">
                  {device.thingArn || 'N/A'}
                </code>
                {device.thingArn && (
                  <button
                    className="cert-info__copy"
                    onClick={() => copyToClipboard(device.thingArn)}
                    title="Copy full value"
                  >📋</button>
                )}
              </div>
            </div>

            <div className="cert-info__note">
              ℹ️ Full certificate details and private keys are securely stored and not displayed for security reasons.
            </div>
          </div>
        </div>

        <div className="ggos-modal__footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

ViewCertificateModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  device: PropTypes.shape({
    thingName: PropTypes.string,
    thingArn: PropTypes.string,
    certificateId: PropTypes.string,
    certificateArn: PropTypes.string,
  }),
};
